'use client';
import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import PublicNav from '@/components/PublicNav';
import HomeFooter from '@/app/components/HomeFooter';
import { Film, Camera, Music, Tv, Video, Image as ImageIcon, Globe, Mic, Award, ChevronRight } from 'lucide-react';
import { CATEGORY_IMAGES } from '@/lib/mediaConfig';

const categories = [
  {
    id: 'cat-short-film', icon: <Film size={24} />, name: 'Short Film',
    entries: '480+', prize: '₹50,000', imgKey: 'short-film',
    desc: 'Narrative fiction films up to 30 minutes. Any genre — drama, comedy, thriller, horror, or experimental.',
    criteria: ['Storytelling & Narrative', 'Direction & Vision', 'Cinematography', 'Performances'],
    maxDuration: '30 min', format: 'MP4 / MOV', maxSize: '2 GB',
  },
  {
    id: 'cat-documentary', icon: <Camera size={24} />, name: 'Documentary',
    entries: '210+', prize: '₹40,000', imgKey: 'documentary',
    desc: 'Non-fiction films exploring real people, events, or social issues. Investigative, observational, or poetic.',
    criteria: ['Research & Depth', 'Storytelling', 'Cinematography', 'Impact'],
    maxDuration: '45 min', format: 'MP4 / MOV', maxSize: '2 GB',
  },
  {
    id: 'cat-music-video', icon: <Music size={24} />, name: 'Music Video',
    entries: '390+', prize: '₹35,000', imgKey: 'music-video',
    desc: 'Visual accompaniment to original or licensed music. Narrative, performance, or abstract styles welcome.',
    criteria: ['Visual Creativity', 'Sync & Rhythm', 'Concept', 'Production Quality'],
    maxDuration: '8 min', format: 'MP4 / MOV', maxSize: '1 GB',
  },
  {
    id: 'cat-web-series', icon: <Tv size={24} />, name: 'Web Series Pilot',
    entries: '150+', prize: '₹45,000', imgKey: 'web-series',
    desc: 'First episode of an original web series. Must establish characters, world, and story hook effectively.',
    criteria: ['Pilot Strength', 'Series Potential', 'Characters', 'Production'],
    maxDuration: '25 min', format: 'MP4 / MOV', maxSize: '2 GB',
  },
  {
    id: 'cat-reels', icon: <Video size={24} />, name: 'Social Reels',
    entries: '620+', prize: '₹25,000', imgKey: 'reels',
    desc: 'Short-form vertical or square content for social platforms. Storytelling, comedy, or creative expression.',
    criteria: ['Concept & Hook', 'Editing Rhythm', 'Engagement', 'Originality'],
    maxDuration: '3 min', format: 'MP4', maxSize: '500 MB',
  },
  {
    id: 'cat-photo', icon: <ImageIcon size={24} />, name: 'Photo Essay',
    entries: '180+', prize: '₹20,000', imgKey: 'photo-essay',
    desc: 'A series of 10–20 photographs telling a cohesive visual story on a single theme or subject.',
    criteria: ['Visual Narrative', 'Composition', 'Lighting', 'Thematic Cohesion'],
    maxDuration: '20 images', format: 'JPG / PNG', maxSize: '100 MB',
  },
  {
    id: 'cat-branded', icon: <Globe size={24} />, name: 'Branded Content',
    entries: '240+', prize: '₹30,000', imgKey: 'branded',
    desc: 'Commercial or brand-sponsored content that tells a story while promoting a product or service.',
    criteria: ['Brand Integration', 'Storytelling', 'Creativity', 'Effectiveness'],
    maxDuration: '5 min', format: 'MP4 / MOV', maxSize: '1 GB',
  },
  {
    id: 'cat-podcast', icon: <Mic size={24} />, name: 'Audio Podcast',
    entries: '130+', prize: '₹15,000', imgKey: 'podcast',
    desc: 'Original audio storytelling — narrative, interview, or documentary podcast episodes.',
    criteria: ['Audio Quality', 'Storytelling', 'Hosting', 'Production'],
    maxDuration: '60 min', format: 'MP3 / WAV', maxSize: '500 MB',
  },
];

export default function CategoriesPage() {
  const [selected, setSelected] = useState<string | null>(null);

  return (
    <div className="min-h-screen bg-background">
      <PublicNav />

      {/* Hero */}
      <section className="relative pt-32 pb-20 px-6 lg:px-10 overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(201,168,76,0.12) 0%, transparent 60%)' }} />
        <div className="max-w-screen-2xl mx-auto relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.4)' }}>
            <Award size={14} className="text-primary" />
            <span className="text-xs font-semibold text-primary tracking-widest uppercase">Season 3 — 2026</span>
          </div>
          <h1 className="text-5xl lg:text-7xl font-extrabold text-foreground mb-6" style={{ letterSpacing: '-0.03em', lineHeight: 1.05 }}>
            Award <span className="gradient-gold-text">Categories</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            18 specialized categories covering every format of modern media creation. Find your category and submit your best work.
          </p>
        </div>
      </section>

      {/* Categories grid */}
      <section className="py-10 px-6 lg:px-10 max-w-screen-2xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {categories?.map((cat) => {
            const img = CATEGORY_IMAGES?.[cat?.imgKey];
            const isOpen = selected === cat?.id;
            return (
              <div
                key={cat?.id}
                className={`glass-card rounded-2xl overflow-hidden cursor-pointer group transition-all duration-300 ${isOpen ? 'ring-1 ring-primary/50 glow-gold-sm' : 'glass-card-hover'}`}
                onClick={() => setSelected(isOpen ? null : cat?.id)}
              >
                <div className="relative h-40 overflow-hidden">
                  <Image
                    src={img?.src || 'https://images.unsplash.com/photo-1485846234645-a62644f84728?w=600&h=400&fit=crop'}
                    alt={img?.alt || cat?.name}
                    fill
                    className="object-cover transition-transform duration-500 group-hover:scale-110"
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-3 left-3 w-10 h-10 rounded-xl bg-primary/90 flex items-center justify-center text-primary-foreground">
                    {cat?.icon}
                  </div>
                  <div className="absolute top-3 right-3 px-2 py-1 rounded-lg text-xs font-bold text-primary-foreground" style={{ background: 'rgba(201,168,76,0.9)' }}>
                    {cat?.prize}
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-foreground mb-1">{cat?.name}</h3>
                  <p className="text-xs text-muted-foreground mb-3">{cat?.entries} entries this season</p>
                  {isOpen && (
                    <div className="border-t border-gold-subtle pt-4 mt-2 space-y-3">
                      <p className="text-xs text-muted-foreground leading-relaxed">{cat?.desc}</p>
                      <div>
                        <p className="text-xs font-bold text-primary mb-2">Judging Criteria</p>
                        <div className="flex flex-wrap gap-1.5">
                          {cat?.criteria?.map((c) => (
                            <span key={c} className="text-xs px-2 py-1 rounded-lg" style={{ background: 'rgba(201,168,76,0.1)', color: 'rgba(201,168,76,0.9)', border: '1px solid rgba(201,168,76,0.2)' }}>{c}</span>
                          ))}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="rounded-lg p-2" style={{ background: 'rgba(255,255,255,0.03)' }}>
                          <p className="text-xs font-bold text-foreground">{cat?.maxDuration}</p>
                          <p className="text-xs text-muted-foreground">Max Length</p>
                        </div>
                        <div className="rounded-lg p-2" style={{ background: 'rgba(255,255,255,0.03)' }}>
                          <p className="text-xs font-bold text-foreground">{cat?.format}</p>
                          <p className="text-xs text-muted-foreground">Format</p>
                        </div>
                        <div className="rounded-lg p-2" style={{ background: 'rgba(255,255,255,0.03)' }}>
                          <p className="text-xs font-bold text-foreground">{cat?.maxSize}</p>
                          <p className="text-xs text-muted-foreground">Max Size</p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-xs font-semibold text-primary">{isOpen ? 'Less info' : 'More info'}</span>
                    <ChevronRight size={14} className={`text-primary transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 lg:px-10">
        <div className="max-w-3xl mx-auto text-center glass-card rounded-3xl p-12" style={{ background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.2)' }}>
          <h2 className="text-3xl font-extrabold text-foreground mb-4" style={{ letterSpacing: '-0.025em' }}>
            Found Your Category?
          </h2>
          <p className="text-muted-foreground mb-8">Entry fee is ₹499 per submission. Early bird discount available until 31 July 2026.</p>
          <Link href="/register" className="btn-primary px-10 py-4 rounded-full font-bold inline-flex items-center gap-2">
            <Award size={18} /> Submit Your Entry
          </Link>
        </div>
      </section>

      <HomeFooter />
    </div>
  );
}
